import React, { memo, useEffect, useRef, useState } from 'react';

import {
  ArrowButtons,
  Breakpoints,
  CustomImage,
  Disclaimer,
  DisclaimerModal,
  Gallery,
  GallerySlide,
  Lentils,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
  useIsMobile,
} from '@vw-marketing/us-components';

import IncentiveConstants from '../../../hooks-store/incentive-store/actions/incentive-constants';
import { useStore } from '../../../hooks-store/store';
import {
  DealerModel,
  OfferInformation,
  OffersModel,
} from '../../../hooks-store/typings/incentive-store';
import { useFeatureAppConfig } from '../../../hooks/use-feature-app-config';
import { useFeatureAppContent } from '../../../hooks/use-feature-app-content';
import { useIsNationalOffers } from '../../../hooks/use-is-national-offer';
import { useModelsConfig } from '../../../hooks/use-models-config';
import { useTrackingManager } from '../../../hooks/use-tracking-manager';
import {
  onHandleBasicFormButtonLink,
  onHandleFormButtonTracking,
  onHandleGalleryLoaded,
  onSlideActionTracking,
} from '../../../tagging/tagging-helpers';
import { AppConstants } from '../../../utils/app-constants';
import { getMSRPValue } from '../../../utils/general';
import { CardContent } from './card-content/CardContent';
import {
  CustomGallerySlideWrapper,
  SalesEventLogoWrapper,
  StyledArrowButtonsContainer,
  StyledDisclaimerContainer,
  StyledGalleryWrapper,
  StyledImageCard,
  StyledLentilsContainer,
  StyledOfferInformationWrapper,
  StyledOffersWrapper,
  StyledSalesEventCardWrapper,
  StyledSalesEventHeaderWrapper,
  StyledSalesOfferCardWrapper,
} from './styled';

interface SalesEventCardProps {
  readonly specialOffers: OfferInformation[];
}

export const SalesEventCard = memo(
  ({ specialOffers = [] }: SalesEventCardProps) => {
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [isFirstSlide, setIsFirstSlide] = useState<boolean>(true);
    const [isLastSlide, setIsLastSlide] = useState<boolean>(false);

    const prevIndexRef = useRef<number>(0);
    const arrowButtonsContainerRef = useRef<HTMLDivElement>(null);

    const [
      store,
      { [IncentiveConstants.SetModalFormData]: setModalFormData },
    ] = useStore();
    const { vehicle } = useFeatureAppConfig();
    const isTablet: boolean = useIsMobile(Breakpoints.b1280);
    const modelOverview = useModelsConfig();
    const trackingManager = useTrackingManager();
    const isNationalOffersOnly = useIsNationalOffers();
    const { sales_event_logo_url_light } = useFeatureAppContent();

    const vehicleDealer: DealerModel = vehicle?.dealer
      ? ({
          ...vehicle.dealer,
        } as any)
      : store.dealer;

    const handleChange = (newActiveIndex: number) => {
      handleTracking(newActiveIndex);

      setIsFirstSlide(newActiveIndex === 0);
      setIsLastSlide(newActiveIndex === specialOffers.length - 1);
    };

    const handleTracking = (activeIndex: number) => {
      const prevIndex = prevIndexRef.current;

      if (activeIndex !== prevIndex) {
        const direction = activeIndex > prevIndex ? 'next' : 'previous';

        setActiveIndex(activeIndex);

        onSlideActionTracking(
          'click',
          direction,
          specialOffers[activeIndex],
          trackingManager,
          undefined,
          store.dealer,
          false,
          undefined,
          true,
        );

        onHandleGalleryLoaded(
          store.dealer,
          specialOffers[activeIndex],
          trackingManager,
        );

        prevIndexRef.current = activeIndex;
      }

      setIsFirstSlide(activeIndex === 0);
      setIsLastSlide(activeIndex === specialOffers.length - 1);
    };

    const goToInventory = (offer: OfferInformation) => {
      const inventoryURL: string = `/en/inventory.html/__app/inventory/results/${offer.slug.replace(
        '.',
        '',
      )}.app`;

      window.open(inventoryURL, '_blank');

      onHandleBasicFormButtonLink(
        offer,
        'View inventory',
        trackingManager,
        store,
        inventoryURL,
      );
    };

    const setNationalOffersOnlyFormData = (offer: OffersModel) => {
      setModalFormData(
        offer,
        undefined,
        undefined,
        AppConstants.NationalOffersOnlyFormType,
        AppConstants.NationalOffersOnlyFormName,
      );
    };

    const isNotStandardModel = (offer: OffersModel) =>
      typeof offer.modelCustomCta !== 'undefined';

    const setNotStandardFormData = (offer: OffersModel) => {
      setModalFormData(
        offer,
        undefined,
        vehicleDealer,
        AppConstants.NotStandardModelFormType,
        AppConstants.NotStandardModelFormName,
      );
    };

    const setFormData = (specialOffer: OffersModel) => {
      if (isNationalOffersOnly) {
        setNationalOffersOnlyFormData(specialOffer);
      } else if (isNotStandardModel(specialOffer)) {
        setNotStandardFormData(specialOffer);
      } else {
        setModalFormData(
          specialOffer,
          undefined,
          vehicleDealer,
          AppConstants.OfferDetailsFormType,
          AppConstants.OfferDetailsFormName,
        );
      }
      onHandleFormButtonTracking(
        specialOffer,
        'View offers details',
        trackingManager,
        store,
      );
    };

    /* useEffect to get the two arrow buttons at first render */
    useEffect(() => {
      // Add a small delay to ensure buttons are rendered
      const timer = setTimeout(() => {
        if (arrowButtonsContainerRef.current) {
          const prevButton = Array.from(
            arrowButtonsContainerRef.current.querySelectorAll('button'),
          ).find(
            button => button.getAttribute('aria-label') === 'To previous offer',
          );

          const nextButton = Array.from(
            arrowButtonsContainerRef.current.querySelectorAll('button'),
          ).find(
            button => button.getAttribute('aria-label') === 'To next offer',
          );

          if (prevButton) {
            prevButton.style.visibility = isFirstSlide ? 'hidden' : 'visible';
            prevButton.style.opacity = isFirstSlide ? '0' : '1';
          }

          if (nextButton) {
            nextButton.style.visibility = isLastSlide ? 'hidden' : 'visible';
            nextButton.style.opacity = isLastSlide ? '0' : '1';
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }, [activeIndex]);

    return (
      <div data-testid="sales-event-card">
        <StyledSalesEventCardWrapper>
          {sales_event_logo_url_light && isTablet && (
            <StyledSalesEventHeaderWrapper>
              <SalesEventLogoWrapper>
                <CustomImage
                  src={sales_event_logo_url_light}
                  alt="Sales_Event_Logo"
                  srcPlaceholder={AppConstants.PlaceHolder480x216 as string}
                />
              </SalesEventLogoWrapper>
            </StyledSalesEventHeaderWrapper>
          )}
          {specialOffers.length > 0 && (
            <StyledOffersWrapper>
              <TextCustom
                appearance={TextAppearance.label100}
                tag={TextTag.span}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize20,
                    lineHeight: TextLineHeight.TextLineHeight32,
                    color: TextColor.inherit,
                    fontWeight: TextWeight.regular,
                  },
                }}
              >
                <TextCustom
                  appearance={TextAppearance.label100}
                  tag={TextTag.span}
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize20,
                      lineHeight: TextLineHeight.TextLineHeight32,
                      color: TextColor.inherit,
                      fontWeight: TextWeight.bold,
                    },
                  }}
                >
                  {activeIndex + 1} of {specialOffers.length}{' '}
                </TextCustom>
                Offers
              </TextCustom>
            </StyledOffersWrapper>
          )}
          <StyledGalleryWrapper>
            <Gallery
              id="offers-gallery"
              aria-label="Sales event gallery"
              ariaLabelledby="gallery-label"
              bottomElements={
                <>
                  {!isTablet && (
                    <StyledArrowButtonsContainer
                      ref={arrowButtonsContainerRef}
                      data-testid="buttons-container"
                    >
                      <ArrowButtons
                        buttonPreviousAriaLabel="To previous offer"
                        buttonNextAriaLabel="To next offer"
                      />
                    </StyledArrowButtonsContainer>
                  )}
                  <StyledLentilsContainer>
                    <Lentils />
                  </StyledLentilsContainer>
                </>
              }
              fadingEdges={isTablet ? 'auto' : 'none'}
              handleChange={(activeIndex: number) => handleChange(activeIndex)}
              gutter="grid001"
            >
              {specialOffers.map((offer: OfferInformation, index: number) => (
                <CustomGallerySlideWrapper>
                  <GallerySlide
                    ariaLabelledby={`Slide ${index + 1}`}
                    aria-label={`Special offer ${index + 1} of ${
                      specialOffers.length
                    }: ${offer?.specialOffer?.dealTitle || ''}`}
                    key={`Special_Offer_${offer?.specialOffer?.dealId}`}
                    data-testid={`Slide ${index + 1}`}
                  >
                    <StyledSalesOfferCardWrapper>
                      <StyledImageCard
                        imageUrl={
                          offer.specialOffer?.customContent?.offerImage || ''
                        }
                      >
                        {offer?.specialOffer?.dealOfferTypes[0].toLowerCase() !==
                          'lease' && (
                          <StyledDisclaimerContainer>
                            <DisclaimerModal
                              disclaimers={
                                ([
                                  {
                                    description:
                                      offer?.specialOffer?.customContent
                                        ?.offerImageDisclaimer || '',
                                    key: '1',
                                    token: '1',
                                    number: '1',
                                  },
                                ] as unknown) as Disclaimer[]
                              }
                            />
                          </StyledDisclaimerContainer>
                        )}
                      </StyledImageCard>
                      <StyledOfferInformationWrapper>
                        <CardContent
                          goToInventory={goToInventory}
                          modelMsrp={getMSRPValue(offer, modelOverview)}
                          offer={offer}
                          setFormData={setFormData}
                        />
                      </StyledOfferInformationWrapper>
                    </StyledSalesOfferCardWrapper>
                  </GallerySlide>
                </CustomGallerySlideWrapper>
              ))}
            </Gallery>
          </StyledGalleryWrapper>
        </StyledSalesEventCardWrapper>
      </div>
    );
  },
);
